import React, { useState, useRef } from 'react';
import { Box, TextField, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import useThemeStyles from '../../theme/useThemeStyles';
import { chatTheme } from '../../theme/chatTheme';
import FunctionCallIndicator from '../Chat/FunctionCallIndicator';

/**
 * AIChatInput - Input component for AI chat with function calling indicators
 * 
 * @param {Object} props
 * @param {Function} props.onSend - Callback when message is sent
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {boolean} props.isFunctionCalling - Whether a function is currently being called
 * @param {string} props.functionCallName - Name of the function being called
 * @param {string} props.placeholder - Placeholder text
 */
const AIChatInput = ({
  onSend,
  disabled = false,
  isFunctionCalling = false,
  functionCallName = null,
  placeholder = 'Type a message...',
}) => {
  const _styles = useThemeStyles();
  const [message, setMessage] = useState('');
  const textFieldRef = useRef(null);

  const handleSend = () => {
    if (message.trim() && !disabled && !isFunctionCalling) {
      onSend(message.trim());
      setMessage('');
      // Reset text field height
      if (textFieldRef.current) {
        const textarea = textFieldRef.current.querySelector('textarea');
        if (textarea) {
          textarea.style.height = 'auto';
        }
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={chatTheme.messageInput.container}>
      {/* Function calling indicator */}
      {isFunctionCalling && (
        <FunctionCallIndicator functionName={functionCallName} />
      )}

      {/* Input area */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        <TextField
          inputRef={textFieldRef}
          fullWidth
          multiline
          maxRows={4}
          placeholder={placeholder}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={disabled || isFunctionCalling}
          sx={chatTheme.messageInput.input}
          InputProps={{
            sx: {
              padding: '8px 16px',
            },
          }}
        />

        <Tooltip title="Send message">
          <span>
            <IconButton
              onClick={handleSend}
              disabled={!message.trim() || disabled || isFunctionCalling}
              sx={chatTheme.messageInput.sendButton}
            >
              {isFunctionCalling ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : (
                <SendIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default AIChatInput;

